/**
 * Service Worker Registration
 * US5: Remote Secure Voting - Progressive Web App (PWA) Support
 *
 * Features:
 * - Offline vote caching and retry
 * - Background sync for failed submissions
 * - Push notifications for vote confirmation
 * - App updates and cache management
 *
 * Browser Support:
 * - Chrome 40+, Firefox 44+, Safari 11.1+, Edge 17+
 * - Mobile: iOS 11.3+, Android Chrome 40+
 */

export interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

/**
 * Register service worker for PWA capabilities
 * Call this from main.tsx after React app initialization
 */
export function register(config?: ServiceWorkerConfig): void {
  if ('serviceWorker' in navigator) {
    // Wait for page load to avoid slowing down initial render
    window.addEventListener('load', () => {
      const swUrl = `${import.meta.env.BASE_URL}sw.js`;

      if (isLocalhost) {
        // In development, check if service worker exists
        checkValidServiceWorker(swUrl, config);

        // Log additional info in development
        navigator.serviceWorker.ready.then(() => {
          console.log(
            '[Service Worker] App is being served from cache by a service worker. ' +
              'For offline capabilities, visit: https://cra.link/PWA'
          );
        });
      } else {
        // In production, just register the service worker
        registerValidSW(swUrl, config);
      }

      // Listen for online/offline events
      window.addEventListener('online', () => {
        console.log('[Service Worker] Back online - syncing queued votes');
        config?.onOnline?.();

        // Notify service worker to retry queued requests
        navigator.serviceWorker.controller?.postMessage({
          type: 'ONLINE',
        });
      });

      window.addEventListener('offline', () => {
        console.log('[Service Worker] Offline mode - votes will be queued');
        config?.onOffline?.();
      });
    });
  } else {
    console.warn('[Service Worker] Not supported in this browser');
  }
}

/**
 * Register the service worker
 */
async function registerValidSW(swUrl: string, config?: ServiceWorkerConfig): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.register(swUrl);

    // Check for service worker updates
    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (installingWorker == null) {
        return;
      }

      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New update available - notify user
            console.log(
              '[Service Worker] New content is available; please refresh to update.'
            );

            // Execute callback
            if (config && config.onUpdate) {
              config.onUpdate(registration);
            }

            // Notify user about update
            showUpdateNotification();
          } else {
            // Content is cached for offline use
            console.log('[Service Worker] Content is cached for offline use.');

            if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          }
        }
      };
    };

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      handleServiceWorkerMessage(event);
    });

    console.log('[Service Worker] Registration successful:', registration.scope);
  } catch (error) {
    console.error('[Service Worker] Registration failed:', error);
  }
}

/**
 * Check if service worker file exists and is valid
 */
async function checkValidServiceWorker(swUrl: string, config?: ServiceWorkerConfig): Promise<void> {
  try {
    // Check if the service worker can be found
    const response = await fetch(swUrl, {
      headers: { 'Service-Worker': 'script' },
    });

    // Ensure service worker exists, and that we really are getting a JS file
    const contentType = response.headers.get('content-type');
    if (
      response.status === 404 ||
      (contentType != null && contentType.indexOf('javascript') === -1)
    ) {
      // No service worker found. Probably a different app. Reload the page.
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      window.location.reload();
    } else {
      // Service worker found. Proceed as normal.
      registerValidSW(swUrl, config);
    }
  } catch (error) {
    console.log('[Service Worker] No internet connection. App is running in offline mode.');
  }
}

/**
 * Unregister service worker
 * Use this for debugging or when disabling PWA features
 */
export async function unregister(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      console.log('[Service Worker] Unregistered successfully');
    } catch (error) {
      console.error('[Service Worker] Unregistration failed:', error);
    }
  }
}

/**
 * Handle messages from service worker
 */
function handleServiceWorkerMessage(event: MessageEvent): void {
  const { type, message } = event.data;

  switch (type) {
    case 'QUEUED_FOR_RETRY':
      console.log('[Service Worker]', message);
      showNotification('Voto en cola', message, 'info');
      break;

    case 'RETRY_SUCCESS':
      console.log('[Service Worker]', message);
      showNotification('Voto enviado', message, 'success');
      break;

    case 'RETRY_FAILED':
      console.error('[Service Worker]', message);
      showNotification('Error', message, 'error');
      break;

    default:
      console.log('[Service Worker] Message:', event.data);
  }
}

/**
 * Show update notification to user
 */
function showUpdateNotification(): void {
  // Check if user wants to update
  const updateAvailable = confirm(
    'Una nueva versión de la aplicación está disponible. ¿Desea actualizar ahora?'
  );

  if (updateAvailable) {
    // Tell service worker to skip waiting
    navigator.serviceWorker.controller?.postMessage({
      type: 'SKIP_WAITING',
    });

    // Reload page after service worker activates
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  }
}

/**
 * Show notification to user
 * In a real app, this would use a toast/notification library
 */
function showNotification(title: string, message: string, type: 'info' | 'success' | 'error'): void {
  // Create notification element
  const notification = document.createElement('div');
  notification.className = `notification notification-${type}`;
  notification.innerHTML = `
    <div style="
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'success' ? '#D1FAE5' : type === 'error' ? '#FEE2E2' : '#FEF3C7'};
      border: 2px solid ${type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#F59E0B'};
      border-radius: 8px;
      padding: 12px 20px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 100;
      animation: slide-up 0.3s ease;
      max-width: 90%;
    ">
      <strong style="display: block; margin-bottom: 4px;">${title}</strong>
      <span>${message}</span>
    </div>
  `;

  document.body.appendChild(notification);

  // Remove after 5 seconds
  setTimeout(() => {
    notification.style.opacity = '0';
    setTimeout(() => document.body.removeChild(notification), 300);
  }, 5000);
}

/**
 * Request notification permission for push notifications
 * Call this when user logs in or first votes
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[Notifications] Not supported in this browser');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    console.log('[Notifications] Permission:', permission);
    return permission;
  }

  return Notification.permission;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      return subscription;
    }

    // Request notification permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Notification permission denied');
      return null;
    }

    // Subscribe to push notifications
    // VAPID public key would come from backend
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });

    console.log('[Push] Subscribed successfully:', subscription);

    // Send subscription to backend
    // await fetch('/api/v1/notifications/subscribe', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(subscription),
    // });

    return subscription;
  } catch (error) {
    console.error('[Push] Subscription failed:', error);
    return null;
  }
}

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData: string = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Check if app is running as installed PWA
 */
export function isRunningAsPWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Get service worker registration status
 */
export async function getServiceWorkerStatus(): Promise<{
  registered: boolean;
  active: boolean;
  waiting: boolean;
  installing: boolean;
}> {
  if (!('serviceWorker' in navigator)) {
    return { registered: false, active: false, waiting: false, installing: false };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      return { registered: false, active: false, waiting: false, installing: false };
    }

    return {
      registered: true,
      active: !!registration.active,
      waiting: !!registration.waiting,
      installing: !!registration.installing,
    };
  } catch (error) {
    return { registered: false, active: false, waiting: false, installing: false };
  }
}
